import React, { ReactNode, useEffect, useRef } from 'react';
import { BottomSheet, BottomSheetRef } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';
import styles from './DeliveryDashboardBottomSheet.module.scss';
import { IconClose } from '@justeattakeaway/pie-icons-react';
import { isPWA } from '@src/shared/utils';
import { useDashboardUIStore } from '../../store/dashboardUIStore';
import { SpringEvent } from 'react-spring-bottom-sheet/dist/types';

interface IDeliveryDashboardBottomSheetProps {
  children?: ReactNode;
  header?: ReactNode | string;
  maxHeight?: number;
  onBottomSheetHeight: (height: number) => void;
  onSpringEnd?: (event: SpringEvent) => void;
  onSpringStart?: (event: SpringEvent) => void;
  onDismiss?: () => void;
  showCloseButton?: Boolean;
  isDetailView?: boolean;
  onClose?: () => void;
  initialSnap?: ({
    headerHeight,
    maxHeight,
  }: {
    headerHeight: number;
    maxHeight: number;
  }) => number;
  onRefInitialized?: (ref: any) => void;
  blocking?: boolean;
  constantHeight?: number;
  className?: string;
  skipInitialTransition?: boolean;
  expandOnContentDrag?: boolean;
  isOpen?: boolean;
  style?: React.CSSProperties;
  sibling?: ReactNode;
}

export const DELIVERY_DASHBOARD_BOTTOM_SHEET_ANIMATION_COMPLETE_GLOBAL_EVENT_NAME =
  'delivery-dashboard-bottom-sheet-animation-complete';

export const DeliveryDashboardBottomSheet = (
  props: IDeliveryDashboardBottomSheetProps,
  ref: React.Ref<BottomSheetRef>
) => {
  const replicaRef = React.createRef<BottomSheetRef>();
  const focusRef = useRef<HTMLButtonElement>(null);
  const contentContainer = useRef(null);
  const { bottomSheetForcedSnapHeight } = useDashboardUIStore();

  const getRef = () => {
    return ref || (replicaRef as any);
  };
  useEffect(() => {
    if (getRef().current) {
      props.onRefInitialized && props.onRefInitialized(replicaRef);
    }
  }, [getRef().current]);

  const calculateSnapPointFinalValue = (maxHeight: number) => {
    const pwaAppScreensContainer = document.getElementById('pwaAppScreensContainer');
    if (pwaAppScreensContainer && isPWA()) {
      return maxHeight - pwaAppScreensContainer.offsetHeight - 180;
    }
    return isPWA() ? maxHeight - 180 : maxHeight - 87;
  };

  const triggerGlobalEvent = () => {
    const event = new CustomEvent(
      DELIVERY_DASHBOARD_BOTTOM_SHEET_ANIMATION_COMPLETE_GLOBAL_EVENT_NAME,
      {
        height: getRef().current?.height,
      } as any
    );
    window.dispatchEvent(event);
  };

  return (
    <BottomSheet
      style={props.style ? { ...props.style } : {}}
      onDismiss={() => {
        props?.onDismiss && props.onDismiss();
      }}
      scrollLocking={true}
      id="delivery-dashboard-bottom-sheet"
      open={props.isOpen !== undefined ? props.isOpen : true}
      ref={ref || replicaRef}
      className={`${props.className || ''} ${styles.root} ${
        props.isDetailView ? styles.detail : ''
      } ${props.constantHeight ? styles.constantHeight : ''}`}
      initialFocusRef={focusRef}
      onSpringStart={(event) => {
        props?.onSpringStart && props.onSpringStart(event);
      }}
      onSpringEnd={(event) => {
        triggerGlobalEvent();
        props.onBottomSheetHeight(getRef().current?.height as number);
        props?.onSpringEnd && props.onSpringEnd(event);
      }}
      defaultSnap={({ headerHeight, maxHeight }) => {
        if (bottomSheetForcedSnapHeight) {
          return bottomSheetForcedSnapHeight;
        } else if (props.constantHeight) {
          return props.constantHeight;
        } else if (props.initialSnap) {
          return props.initialSnap({ headerHeight, maxHeight });
        }
        return headerHeight;
      }}
      snapPoints={({ maxHeight, headerHeight }) => {
        if (bottomSheetForcedSnapHeight) {
          return bottomSheetForcedSnapHeight;
        }
        if (!props.constantHeight) {
          return [headerHeight, maxHeight / 2, calculateSnapPointFinalValue(maxHeight)];
        } else {
          return [props.constantHeight, props.constantHeight];
        }
      }}
      sibling={props.sibling || null}
      skipInitialTransition={!!props.skipInitialTransition}
      blocking={props.blocking || false}
      expandOnContentDrag={
        props.expandOnContentDrag !== undefined ? props.expandOnContentDrag : true
      }
      header={
        <div className={styles.bottomSheetHeader}>
          {props.header}
          {props.showCloseButton && (
            <div
              id="delivery-dashboard-bottom-sheet-close-button"
              onClick={() => {
                getRef().current?.snapTo(0);
                window.requestAnimationFrame(() => {
                  props.onClose && props?.onClose();
                });
              }}
            >
              <IconClose className={styles.bottomSheetCloseButton} size="s" />
            </div>
          )}
        </div>
      }
    >
      <div ref={contentContainer}>{props.children}</div>
    </BottomSheet>
  );
};

export default React.forwardRef(DeliveryDashboardBottomSheet);
